import { useEffect, useState } from "react";
import CheckboxInput from "../../../../components/inputs/checkbox";
import SwitchInput from "../../../../components/inputs/checkbox/switch";
import FileInput from "../../../../components/inputs/file";
import NumberInput from "../../../../components/inputs/number";
import OutputTransmedForm from "../../../../dtos/transmed/process/form";
import TransmedProcessSlide from "./slide";
import OutputTransmedReceiving from "../../../../dtos/transmed/process/receiving";
import { TransmedRegistrationStage } from "../../../../dtos/transmed/process/registration/registration";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faChevronUp,
  faCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { useSetError } from "../../../generic-error";
import { useRevalidator } from "react-router-dom";
import TransmedRegistrationService from "../../../../service/transmed/process/registration/registration";
import { CircularProgress } from "@mui/material";
import OutputTransmedFormSection from "../../../../dtos/transmed/process/form/section";
import InputDialog from "../../../../components/dialog";
import useWindowDimensions from "../../../../hooks/useWindowDimensions";
import subtractOneSecond from "../../../../utils/subtractOneSecond";
import TransmedRegistrationFieldService from "../../../../service/transmed/process/registration/field";

type PropsType = {
  periodText: string;
  form: OutputTransmedForm;
  receivings: OutputTransmedReceiving[];
  registration_end: Date;
  onSubmit?: () => void;
};

function TransmedProcessRegistration(props: PropsType) {
  const { width } = useWindowDimensions();
  const revalidator = useRevalidator();
  const setErrorOnly = useSetError();
  const setError = (error: unknown) => {
    setErrorOnly(error);
    revalidator.revalidate();
  };
  const [loading, setLoading] = useState<boolean>(false);

  const [coordinator, setCoordinator] = useState<boolean>(false);

  const [sectionId, setSectionId] = useState<number | null>(null);
  const [subsectionId, setSubsectionId] = useState<number | null>(null);

  const [registrationFields, setRegistrationFields] = useState<{
    [key: number]: { quantity: number; file: File | null };
  }>({});
  const [selectedLc, setSelectedLc] = useState<string | null>(null);
  const [selectedLcAccomodation, setSelectedLcAccomodation] = useState(false);

  const [dialogCallback, setDialogCallback] = useState<
    (() => Promise<void> | void) | null
  >(null);

  useEffect(() => {
    if (props.form.registration?.stage === TransmedRegistrationStage.returned)
      setError(
        `Înscrierea ta a avut probleme. Poți modifica datele și reîncărca până în ${subtractOneSecond(
          props.registration_end
        ).toLocaleDateString("RO-ro")}, ora ${subtractOneSecond(
          props.registration_end
        ).toLocaleTimeString("RO-ro")}.`
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.form.registration !== null) {
      setRegistrationFields((rfs) => {
        let obj: {
          [key: number]: { quantity: number; file: File | null };
        } = {};
        for (const rf of props.form.registration
          ? props.form.registration.fields
          : []) {
          obj[rf.field] = {
            quantity: rf.quantity,
            file: rfs[rf.field] ? rfs[rf.field].file : null,
          };
        }
        return obj;
      });
    }
  }, [props.form.registration, props.form.registration?.fields]);

  useEffect(() => {
    if (props.form.registration) {
      if (!props.form.registration || !props.form.registration.preferred_lc) {
        setSelectedLc(null);
        return;
      }

      setSelectedLc(props.form.registration.preferred_lc.id);
      setSelectedLcAccomodation(
        props.form.registration!.preferred_lc_accomodation
      );
    }
  }, [props.form.registration]);

  const [submitted, setSubmitted] = useState<boolean>(false);
  useEffect(() => {
    if (submitted && revalidator.state === "idle") {
      setSubmitted(false);
      if (props.onSubmit) props.onSubmit();
    }
  }, [props, revalidator.state, submitted]);

  function getPoints(section: OutputTransmedFormSection): string {
    return Object.keys(registrationFields)
      .map(Number)
      .reduce((acc, key) => {
        for (const ss of section.subsections)
          for (const f of ss.fields)
            if (f.id === key)
              return acc + f.points * registrationFields[key].quantity;
        return acc;
      }, 0)
      .toFixed(2);
  }

  const isDisabled = () =>
    props.registration_end < new Date() ||
    (props.form.registration
      ? props.form.registration?.stage !==
          TransmedRegistrationStage.incomplete &&
        props.form.registration?.stage !== TransmedRegistrationStage.returned
      : false);

  const onSaveLc = async () => {
    if (selectedLc !== null)
      await new TransmedRegistrationService().create.lcSave(
        selectedLc,
        selectedLcAccomodation
      );
  };

  const onSaveLcSafe = async () => {
    setLoading(true);
    setError("");

    try {
      await onSaveLc();
    } catch (error) {
      setError(error);
    } finally {
      revalidator.revalidate();
      setLoading(false);
    }
  };

  const onSaveFiles = async () => {
    await new TransmedRegistrationFieldService().create.fileSave(
      Object.entries(registrationFields)
        .filter((entry) => entry[1].quantity > 0)
        .map((entry) => ({
          id: Number(entry[0]),
          quantity: entry[1].quantity,
          file: entry[1].file,
        }))
    );
  };

  const onSaveFilesSafe = async () => {
    setLoading(true);
    setError("");

    try {
      await onSaveFiles();
    } catch (error) {
      setError(error);
    } finally {
      revalidator.revalidate();
      setLoading(false);
    }
  };

  return (
    <TransmedProcessSlide style={{ width: "100%" }}>
      <h4>înscrierile au început pentru ediția:</h4>
      <h1
        style={{
          overflowWrap: "break-word",
          wordBreak: "break-word",
        }}
      >
        {props.periodText}
      </h1>
      <div className="flex row">
        {props.form.coordinator && (
          <>
            <SwitchInput
              disabled={isDisabled()}
              value={coordinator}
              onChange={(e) => setCoordinator(e.target.checked)}
            />
            <span>Aplică pe locurile destinate coordonatorilor</span>
          </>
        )}
      </div>
      {!coordinator && (
        <form
          className="surface"
          style={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: "var(--gap-small)",
            marginTop: "var(--gap-big)",
          }}
          onSubmit={async (e) => {
            e.preventDefault();

            await onSaveFilesSafe();
          }}
        >
          <h3>Încarcă documentele pentru înscriere</h3>
          {props.form.sections.map((section) => {
            return (
              <div
                key={section.id}
                className="surface"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--gap-small)",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    overflowWrap: "break-word",
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      minWidth: 0,
                    }}
                  >
                    <h4 style={{ width: "90px", flexShrink: 0 }}>
                      <b>
                        {(
                          (section.weight /
                            props.form.sections.reduce(
                              (acc, curr) => acc + curr.weight,
                              0
                            )) *
                          props.form.max_points
                        ).toFixed(0)}
                        %
                      </b>
                    </h4>
                    <h4
                      style={{
                        overflowWrap: "break-word",
                        minWidth: 0,
                      }}
                    >
                      {section.title}
                    </h4>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      flex: 1,
                      justifyContent: "flex-end",
                    }}
                  >
                    <b
                      style={{
                        fontWeight: "var(--title-font-weight)",
                        color: "var(--color-fasmr-blue)",
                        marginRight: "var(--gap-xs)",
                      }}
                    >
                      {getPoints(section)}
                    </b>
                    <button
                      type="button"
                      className="primary text button"
                      onClick={() =>
                        setSectionId((sid) =>
                          sid === section.id ? null : section.id
                        )
                      }
                    >
                      <FontAwesomeIcon
                        icon={
                          section.id === sectionId ? faChevronUp : faChevronDown
                        }
                      />
                    </button>
                  </div>
                </div>
                {section.id === sectionId && (
                  <>
                    {section.subsections.map((subsection) => (
                      <div
                        key={subsection.id}
                        className="surface"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "var(--gap-small)",
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "left",
                            flexDirection: "row",
                            flexWrap: width > 330 ? "nowrap" : "wrap",
                            minWidth: 0,
                            gap: "var(--gap-small) 0",
                          }}
                        >
                          <div
                            style={{
                              width: "calc(90px - var(--surface-padding))",
                              flexShrink: 0,
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faCircle}
                              color={
                                subsection.fields.find(
                                  (f) =>
                                    registrationFields[f.id] !== undefined &&
                                    registrationFields[f.id].quantity !== 0
                                ) !== undefined
                                  ? "var(--color-fasmr-blue)"
                                  : "transparent"
                              }
                            />
                          </div>
                          <b
                            style={{
                              flexGrow: 1,
                              minWidth: 0,
                              overflowWrap: "break-word",
                            }}
                          >
                            {subsection.title}
                          </b>
                          <button
                            type="button"
                            className="primary text button"
                            onClick={() =>
                              setSubsectionId((ssid) =>
                                ssid === subsection.id ? null : subsection.id
                              )
                            }
                          >
                            <FontAwesomeIcon
                              icon={
                                subsection.id === subsectionId
                                  ? faChevronUp
                                  : faChevronDown
                              }
                            />
                          </button>
                        </div>
                        {subsection.id === subsectionId &&
                          subsection.fields.map((field) => {
                            return (
                              <div
                                key={field.id}
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "flex-start",
                                  justifyContent: "stretch",
                                  padding: 0,
                                  overflow: "hidden",
                                }}
                                className="surface"
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    padding: "var(--surface-padding)",
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "flex-start",
                                    gap: "var(--gap-small)",
                                  }}
                                  className="inline-scrollbar x registration-field-scrollable"
                                >
                                  <div
                                    style={{
                                      width:
                                        "calc(90px - 3 * var(--surface-padding))",
                                      flexShrink: 0,
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                      justifyContent: "flex-start",
                                    }}
                                  >
                                    <div>{field.points}p</div>
                                    {field.quantitative && (
                                      <div
                                        className="label"
                                        style={{ wordBreak: "break-word" }}
                                      >
                                        /{field.unit_singular}
                                      </div>
                                    )}
                                  </div>
                                  <div
                                    style={{
                                      flex: 1,
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "stretch",
                                    }}
                                  >
                                    <div>{field.title}</div>
                                    <div className="label">
                                      {field.subtitle}
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "stretch",
                                        justifyContent: "flex-start",
                                        gap: "var(--gap-xs)",
                                        marginTop: "var(--gap-small)",
                                      }}
                                    >
                                      {field.quantitative ? (
                                        <NumberInput
                                          disabled={isDisabled()}
                                          placeholder={
                                            "Nr. " + field.unit_plural
                                          }
                                          variant="outlined"
                                          sx={{
                                            width: "100px",
                                            position: "relative",
                                            "& .MuiOutlinedInput-root": {
                                              p: 0,
                                            },
                                          }}
                                          value={
                                            registrationFields[field.id]
                                              ? registrationFields[field.id]
                                                  .quantity
                                              : ""
                                          }
                                          onChange={(e) =>
                                            setRegistrationFields((rf) => ({
                                              ...rf,
                                              [field.id]: {
                                                quantity: isNaN(
                                                  parseFloat(e.target.value)
                                                )
                                                  ? 0
                                                  : parseFloat(e.target.value),
                                                file: rf[field.id]
                                                  ? rf[field.id].file
                                                  : null,
                                              },
                                            }))
                                          }
                                        />
                                      ) : (
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            width: "90px",
                                            flexShrink: 0,
                                            backgroundColor:
                                              "var(--color-overlay)",
                                            borderRadius:
                                              "var(--border-radius-xs)",
                                            border: "1px solid dark",
                                          }}
                                        >
                                          <CheckboxInput
                                            disabled={isDisabled()}
                                            // label="Select"
                                            checked={
                                              registrationFields[field.id]
                                                ? registrationFields[field.id]
                                                    .quantity === 1
                                                : false
                                            }
                                            onChange={(e) =>
                                              setRegistrationFields((rf) => ({
                                                ...rf,
                                                [field.id]: {
                                                  quantity: e.target.checked
                                                    ? 1
                                                    : 0,
                                                  file: rf[field.id]
                                                    ? rf[field.id].file
                                                    : null,
                                                },
                                              }))
                                            }
                                          />
                                        </div>
                                      )}
                                      <FileInput
                                        remoteValue={
                                          props.form.registration?.fields.find(
                                            (rf) => rf.field === field.id
                                          )?.proof ?? undefined
                                        }
                                        types={["pdf", "PDF"]}
                                        disabled={isDisabled()}
                                        style={{ flex: 1 }}
                                        onChange={(files) =>
                                          setRegistrationFields((rf) => ({
                                            ...rf,
                                            [field.id]: {
                                              quantity: rf[field.id]
                                                ? rf[field.id].quantity
                                                : files.length > 0
                                                ? 0
                                                : 1,
                                              file:
                                                files.length > 0
                                                  ? files[0]
                                                  : null,
                                            },
                                          }))
                                        }
                                      />
                                    </div>
                                    {(() => {
                                      const rfield =
                                        props.form.registration?.fields.find(
                                          (rf) => rf.field === field.id
                                        );
                                      if (!rfield) return <></>;

                                      return (
                                        <>
                                          {rfield.validator !== null &&
                                            rfield.valid && (
                                              <div
                                                className="label"
                                                style={{
                                                  marginTop: "var(--gap-xs)",
                                                  color: "green",
                                                }}
                                              >
                                                <FontAwesomeIcon
                                                  icon={faCheck}
                                                />{" "}
                                                Validat
                                              </div>
                                            )}
                                          {rfield.answer !== null && (
                                            <div
                                              className="label"
                                              style={{
                                                marginTop: "var(--gap-xs)",
                                              }}
                                            >
                                              <FontAwesomeIcon
                                                icon={faExclamationTriangle}
                                                color="yellow"
                                              />{" "}
                                              {rfield.answer}
                                            </div>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    ))}

                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <h4>Puncte acumulate</h4>
                      <h1 style={{ color: "var(--color-fasmr-blue)" }}>
                        {getPoints(section)}
                      </h1>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          <button
            type="submit"
            disabled={isDisabled()}
            className="secondary button"
            style={{ margin: "var(--gap-medium) 0", alignSelf: "center" }}
          >
            {(loading || revalidator.state === "loading") && (
              <CircularProgress sx={{ color: "inherit" }} />
            )}{" "}
            Salvează documentele
          </button>
        </form>
      )}
      <form
        className="surface"
        style={{
          alignSelf: "stretch",
          display: "flex",
          flexDirection: "column",
          gap: "var(--gap-small)",
          marginTop: "var(--gap-small)",
        }}
        onSubmit={async (e) => {
          e.preventDefault();
          await onSaveLcSafe();
        }}
      >
        <h3>Stabilește unde vrei să mergi</h3>
        <span>
          Dacă ai punctaj suficient de mare și dacă mai sunt locuri, vei fi
          repartizat automat la această opțiune. În caz contrar, vei putea alege
          din lista opțiunilor rămase disponibile, conform clasamentului
          național.
        </span>
        <div
          style={{
            marginTop: "var(--gap-medium)",
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-evenly",
            gap: "var(--gap-small)",
          }}
        >
          {props.receivings.map((receiving) => (
            <div
              key={receiving.id}
              className="surface"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "var(--gap-xs)",
              }}
            >
              <h4>{receiving.lc.city}</h4>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: "var(--gap-xs)",
                }}
              >
                <button
                  type="button"
                  disabled={isDisabled()}
                  className="button primary text"
                  style={{
                    ...(selectedLcAccomodation && selectedLc === receiving.lc.id
                      ? {
                          backgroundColor: "var(--color-fasmr-blue)",
                          color: "white",
                        }
                      : {}),
                    ...((
                      props.form.registration?.preferred_lc ?? { city: null }
                    ).city === receiving.lc.city &&
                    props.form.registration?.preferred_lc_accomodation
                      ? {
                          border: "2px solid var(--color-fasmr-blue)",
                        }
                      : {}),
                  }}
                  onClick={() => {
                    if (
                      selectedLcAccomodation &&
                      selectedLc === receiving.lc.id
                    ) {
                      setSelectedLc(null);
                      return;
                    }
                    setSelectedLc(receiving.lc.id);
                    setSelectedLcAccomodation(true);
                  }}
                >
                  Cu cazare
                </button>
                <button
                  type="button"
                  disabled={isDisabled()}
                  className="button primary text"
                  style={{
                    ...(!selectedLcAccomodation &&
                    selectedLc === receiving.lc.id
                      ? {
                          backgroundColor: "var(--color-fasmr-blue)",
                          color: "white",
                        }
                      : {}),
                    ...((
                      props.form.registration?.preferred_lc ?? { city: null }
                    ).city === receiving.lc.city &&
                    !props.form.registration?.preferred_lc_accomodation
                      ? {
                          border: "2px solid var(--color-fasmr-blue)",
                        }
                      : {}),
                  }}
                  onClick={() => {
                    if (
                      !selectedLcAccomodation &&
                      selectedLc === receiving.lc.id
                    ) {
                      setSelectedLc(null);
                      return;
                    }
                    setSelectedLc(receiving.lc.id);
                    setSelectedLcAccomodation(false);
                  }}
                >
                  Fără cazare
                </button>
              </div>
            </div>
          ))}
        </div>
        <button
          type="submit"
          className="secondary button"
          style={{ margin: "var(--gap-medium) 0", alignSelf: "center" }}
          disabled={isDisabled()}
        >
          {(loading || revalidator.state === "loading") && (
            <CircularProgress sx={{ color: "inherit" }} />
          )}{" "}
          Salvează prima ta opțiune
        </button>
      </form>
      <button
        type="button"
        className="button primary"
        style={{ margin: "var(--gap-xl) 0", alignSelf: "center" }}
        disabled={isDisabled()}
        onClick={() =>
          setDialogCallback(() => async () => {
            setLoading(true);
            setError("");

            try {
              if (props.form.registration === null)
                throw new Error(
                  "Completează formularul înainte de a te înscrie."
                );

              await onSaveFiles();
              await onSaveLc();
              await new TransmedRegistrationService().update.submit(
                props.form.registration.id,
                coordinator
              );
              revalidator.revalidate();
              setSubmitted(true);
            } catch (error) {
              setError(error);
            } finally {
              setLoading(false);
            }
          })
        }
      >
        {(loading || revalidator.state === "loading") && (
          <CircularProgress sx={{ color: "inherit" }} />
        )}{" "}
        Înscrie-te la Transmed
      </button>
      <h4>
        Termen înscrieri:{" "}
        <b>
          {subtractOneSecond(props.registration_end).toLocaleDateString(
            "RO-ro"
          )}
          , ora{" "}
          {subtractOneSecond(props.registration_end).toLocaleTimeString(
            "RO-ro"
          )}
        </b>
      </h4>
      <InputDialog
        title="Înscrie-te"
        continue_label="Trimite"
        abort_label="Anulează"
        dialogCallback={dialogCallback}
        onClose={() => setDialogCallback(null)}
      >
        Ești sigur că dorești să trimiți înscrierea? Odată trimisă, înscrierea
        nu mai poate fi modificată. Dacă vrei să revii, mai ai timp până în{" "}
        {subtractOneSecond(props.registration_end).toLocaleDateString("RO-ro")},
        ora{" "}
        {subtractOneSecond(props.registration_end).toLocaleTimeString("RO-ro")}{" "}
        pentru a te putea înscrie.
      </InputDialog>
    </TransmedProcessSlide>
  );
}

export default TransmedProcessRegistration;
